import { computed, inject, Injectable, signal } from '@angular/core';
import { SectorService } from '../../core/sector/sector.service';
import { EMPTY, finalize, map, of, tap } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { MainLayoutStore } from './main-layout.store';
import { rxResource } from '@angular/core/rxjs-interop';
import { FaqService } from '../../core/faq/faq.service';
import { FaqQuestionUi } from '../../core/faq/faq.model';
import { AiService } from '../../core/ai/ai.service';
import { AiQuestionUi } from '../../core/ai/ai.model';
import { Audience } from './audience-form/audience-form.model';

@Injectable()
export class MainLayoutFacade {
  readonly #sectorService = inject(SectorService);
  readonly #faqService = inject(FaqService);
  readonly #aiService = inject(AiService);
  readonly #mainLayoutStore = inject(MainLayoutStore);

  sectors$ = this.#sectorService.getSectors('');
  readonly EMPTY_LIST = { list: [], count: 0 };

  readonly lastFaqQuestions = signal<{ count: number; list: FaqQuestionUi[] }>(this.EMPTY_LIST);

  faqQuestions = rxResource({
    params: () => ({
      enabled: this.#mainLayoutStore.activeItemIndex() === 0,
      query: this.#mainLayoutStore.query(),
      limit: this.#mainLayoutStore.limit(),
      offset: this.#mainLayoutStore.offset()
    }),
    stream: ({ params }) => {
      if (!params.enabled) {
        return of(this.EMPTY_LIST);
      }

      return this.#faqService.getFaq(params).pipe(
        tap((faqList) => this.#mainLayoutStore.updateCount(faqList.count)),
        map((faqList) => ({
          ...faqList,
          list: faqList.list.map((question) => new FaqQuestionUi(question))
        })),
        tap((faqList) => this.lastFaqQuestions.set(faqList))
      );
    }
  });

  aiQuestions = rxResource({
    params: () => ({
      enabled: this.#mainLayoutStore.activeItemIndex() === 1,
      query: '',
      limit: 5,
      offset: 0
    }),
    stream: ({ params }) => {
      if (!params.enabled) {
        return of(this.EMPTY_LIST);
      }

      return this.#aiService.getQuestions(params).pipe(
        map((aiList) => ({
          ...aiList,
          list: aiList.list.map((question) => new AiQuestionUi(question))
        }))
      );
    }
  });

  isAskAiDisabled = computed(() => {
    const aiQuestions = this.aiQuestions.value();

    if (!aiQuestions?.list.length) {
      return false;
    }

    return aiQuestions.list.some(
      (question) =>
        question.isAnimated() || question.status === 'processing' || question.status === 'new'
    );
  });

  askAi(question: string) {
    this.#mainLayoutStore.updateIsCreatingAiQuestion(true);

    return this.#aiService.createQuestion(question).pipe(
      tap(() => this.aiQuestions.reload()),
      finalize(() => this.#mainLayoutStore.updateIsCreatingAiQuestion(false))
    );
  }

  getSegments(sectorId: number) {
    return this.#sectorService.getSegments(sectorId);
  }

  buildAudience(audienceForm: FormGroup<Audience>) {
    const { segments, platform, comment } = audienceForm.getRawValue();

    if (segments == null || segments.length === 0 || platform == null) {
      return EMPTY;
    }

    this.#mainLayoutStore.updateIsLoading(true);
    return this.#sectorService
      .createSegment({ segments: segments.map((segment) => segment.id), platform, comment })
      .pipe(finalize(() => this.#mainLayoutStore.updateIsLoading(false)));
  }
}
